/**
 * Workshop identity pill used in modal headers (Brief / Asset Locker /
 * Log). Colours come from the per-workshop tag accent map so WS1…WS8
 * each read as a distinct workstream.
 */

import type { CSSProperties } from "react";
import { WORKSHOPS } from "@/content/workshops";
import { workshopAccent } from "@/lib/tags";

const FALLBACK_STYLE: CSSProperties = {
  padding: "1px 8px",
  background: "rgba(var(--accent-sky-rgb), 0.08)",
  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
  borderRadius: 10,
  fontSize: "var(--fs-micro)",
  color: "var(--accent-sky)",
  fontWeight: 600,
  letterSpacing: 1,
};

/** Inline styles for a workshop-coloured label pill / footer text. */
export function workshopLabelStyle(workshopId: string): CSSProperties {
  const accent = workshopAccent(workshopId);
  if (!accent) return FALLBACK_STYLE;
  return {
    padding: "1px 8px",
    background: `rgba(${accent.rgb}, 0.08)`,
    border: `1px solid rgba(${accent.rgb}, 0.25)`,
    borderRadius: 10,
    fontSize: "var(--fs-micro)",
    color: accent.fg,
    fontWeight: 600,
    letterSpacing: 1,
  };
}

/** Foreground colour only — for modal footer workshop text. */
export function workshopLabelColor(workshopId: string): string {
  return workshopAccent(workshopId)?.fg ?? "var(--accent-sky)";
}

function defaultLabel(workshopId: string): string {
  const id = workshopId.trim().toUpperCase();
  const workshop = WORKSHOPS.find((w) => w.id === id);
  if (!workshop) return workshopId;
  const title = workshop.title.replace(/\n/g, " ").toUpperCase();
  return `${workshop.id} — ${title}`;
}

export function WorkshopLabelPill({
  workshopId,
  label,
  className,
}: {
  workshopId: string;
  /** Override body copy (default: `WS4 — WAYS OF WORKING`). */
  label?: string;
  className?: string;
}) {
  const accent = workshopAccent(workshopId);
  return (
    <span
      className={className ? `tag-chip ${className}` : "tag-chip"}
      data-tag-accent={accent?.key}
      style={workshopLabelStyle(workshopId)}
    >
      {label ?? defaultLabel(workshopId)}
    </span>
  );
}
